import React, { useState, useEffect, useMemo } from 'react';
import '../App.css';
import SearchableSelect from './SearchableSelect';
import {
  PER_DIEM_SUPERVISOR_RATE,
  PER_DIEM_TEAM_MEMBER_RATE,
  calculatePerDiemTotal,
} from '../pricing/perDiem';
import {
  formatCurrencyInput,
  normalizeCurrencyOnBlur,
  parseCurrencyInput,
} from '../pricing/currencyInput';
import { formatDisplayInstantDate } from '../utils/displayDate';

function Expenses({ onSaveExpense, savedCalculations = [], savedExpenses = [], proposalsApiUrl }) {
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [expenseMode, setExpenseMode] = useState('project'); // 'project' | 'program'
  const [projectNumber, setProjectNumber] = useState('');
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [includedEventProjects, setIncludedEventProjects] = useState([]);
  const [clientName, setClientName] = useState('');
  const [reportDate, setReportDate] = useState(getTodayDate());
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [reportPreparedBy, setReportPreparedBy] = useState('');
  const [deliverySupervisor, setDeliverySupervisor] = useState('');
  const [deliveryTeamMembers, setDeliveryTeamMembers] = useState('');
  
  // Lodging
  const [lodgingRooms, setLodgingRooms] = useState('');
  const [lodgingNights, setLodgingNights] = useState('');
  const [lodgingTotal, setLodgingTotal] = useState('');
  
  // Gas
  const [gasTotal, setGasTotal] = useState('');
  
  // Flights
  const [flightsCount, setFlightsCount] = useState('');
  const [flightsTotal, setFlightsTotal] = useState('');
  
  // Per Diem / Day Rate — supervisors ($200) and delivery team members ($175)
  const [perDiemSupervisors, setPerDiemSupervisors] = useState('');
  const [perDiemTeamMembers, setPerDiemTeamMembers] = useState('');
  const [perDiemTotal, setPerDiemTotal] = useState('');
  const [perDiemTotalManuallySet, setPerDiemTotalManuallySet] = useState(false);
  
  // Truck Rental
  const [truckRentalRequired, setTruckRentalRequired] = useState('');
  const [truckRentalCount, setTruckRentalCount] = useState('');
  const [truckRentalTotal, setTruckRentalTotal] = useState('');
  
  // Other expenses (array of { name, total })
  const [otherExpenses, setOtherExpenses] = useState([{ name: '', total: '' }]);
  
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load available projects (prefer proposals API for program fields; fall back to Delivery getProjects)
  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalsApiUrl]);

  // Auto-calculate Per Diem total when supervisor / team member counts change
  useEffect(() => {
    if (!perDiemTotalManuallySet) {
      const nights = parseInt(lodgingNights) || 0;
      const days = nights > 0 ? nights + 1 : 1; // nights + 1; default 1 day
      const calculatedTotal = calculatePerDiemTotal(
        perDiemSupervisors,
        perDiemTeamMembers,
        days
      );
      if (calculatedTotal > 0) {
        setPerDiemTotal(calculatedTotal.toFixed(2));
      } else if (!perDiemSupervisors && !perDiemTeamMembers) {
        setPerDiemTotal('');
      }
    }
  }, [perDiemSupervisors, perDiemTeamMembers, perDiemTotalManuallySet, lodgingNights]);

  const normalizeDateValue = (value) => {
    if (!value) return '';
    if (value instanceof Date) {
      const year = value.getUTCFullYear();
      const month = String(value.getUTCMonth() + 1).padStart(2, '0');
      const day = String(value.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    if (typeof value === 'string') {
      if (value.includes('T')) return value.split('T')[0];
      if (value.match(/^\d{4}-\d{2}-\d{2}$/)) return value;
    }
    return String(value);
  };

  const loadProjects = async () => {
    const deliveryApiUrl = process.env.REACT_APP_DELIVERY_API_URL || '';
    setLoadingProjects(true);
    try {
      let proposals = [];

      // Prefer proposals API (includes programId / programName, same source as Calculator).
      if (proposalsApiUrl) {
        const endpoints = [
          `${proposalsApiUrl}?action=getProposals`,
          `${proposalsApiUrl}?action=getAllProposals`,
          `${proposalsApiUrl}`,
        ];
        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint);
            const result = await response.json();
            if (result.success && (result.proposals || result.data)) {
              proposals = result.proposals || result.data || [];
              console.log('[Expenses] Loaded proposals from', endpoint, proposals.length);
              break;
            }
          } catch (e) {
            console.warn('[Expenses] Proposals endpoint failed:', endpoint, e);
          }
        }
      }

      // Fall back to Delivery Portal getProjects (expense-qualified list).
      if (!proposals.length && deliveryApiUrl) {
        const endpoint = `${deliveryApiUrl}?action=getProjects`;
        console.log('[Expenses] Falling back to Delivery getProjects:', endpoint);
        const response = await fetch(endpoint);
        const result = await response.json();
        if (!result.success) {
          console.error('[Expenses] API returned error:', result.error);
          setAvailableProjects([]);
          return;
        }
        proposals = result.proposals || [];
      }

      if (!proposals.length && !deliveryApiUrl && !proposalsApiUrl) {
        console.warn('[Expenses] No API URL configured — projects will not load');
        setAvailableProjects([]);
        return;
      }

      console.log('[Expenses] Total proposals received:', proposals.length);
      
      // Debug: Log first few proposals to see if label is included
      if (proposals.length > 0) {
        console.log('[Expenses] Sample proposal (first):', JSON.stringify(proposals[0], null, 2));
        if (proposals.length > 1) {
          console.log('[Expenses] Sample proposal (second):', JSON.stringify(proposals[1], null, 2));
        }
      }
      
      // Helper function to check if a date has passed (comparing dates only, ignoring time)
      const hasDatePassed = (dateStr) => {
        if (!dateStr) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of today
        
        let compareDate;
        if (dateStr instanceof Date) {
          compareDate = new Date(dateStr);
        } else if (typeof dateStr === 'string') {
          // If it's YYYY-MM-DD format, parse as local date
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateStr.split('-').map(Number);
            compareDate = new Date(year, month - 1, day);
          } else {
            compareDate = new Date(dateStr);
          }
        } else {
          return false;
        }
        
        compareDate.setHours(0, 0, 0, 0); // Set to start of day
        return compareDate < today;
      };
      
      // Log status distribution for debugging
      const statusCounts = {};
      proposals.forEach(p => {
        const status = (p.status || 'Unknown').trim();
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      console.log('[Expenses] Status distribution:', statusCounts);
      
      // Filter projects based on status and event date criteria
      const qualifyingProjects = proposals.filter(p => {
        // Must have project number and client name
        if (!p.projectNumber || !p.clientName) {
          return false;
        }
        
        const status = (p.status || '').trim();
        const isConfirmed = status.toLowerCase() === 'confirmed';
        const isCompleted = status.toLowerCase() === 'completed';
        
        // Include if status is Completed
        if (isCompleted) {
          return true;
        }
        
        // Include if status is Confirmed AND event date has passed
        if (isConfirmed) {
          const eventDate = p.startDate || p.endDate || '';
          return hasDatePassed(eventDate);
        }
        
        // Exclude all other statuses (Pending, Approved, Cancelled, etc.)
        return false;
      });
      
      console.log('[Expenses] Qualifying projects after filtering:', qualifyingProjects.length, 'out of', proposals.length);
        
      const projects = qualifyingProjects
        .map(p => ({
          projectNumber: String(p.projectNumber).trim(),
          clientName: p.clientName || '',
          venueName: p.venueName || '',
          label: p.label || '',
          startDate: normalizeDateValue(p.startDate),
          endDate: normalizeDateValue(p.endDate),
          status: p.status || '',
          version: p.version || 1,
          programId: p.programId ? String(p.programId).trim() : '',
          programName: p.programName ? String(p.programName).trim() : '',
        }))
        .reduce((acc, p) => {
          // Keep the highest version of each project
          const existingIndex = acc.findIndex(proj => proj.projectNumber === p.projectNumber);
          if (existingIndex === -1) {
            acc.push(p);
          } else if ((p.version || 1) > (acc[existingIndex].version || 1)) {
            acc[existingIndex] = p;
          }
          return acc;
        }, [])
        .sort((a, b) => {
          const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
          if (aDate !== bDate) {
            return bDate - aDate;
          }
          const aNum = parseInt(a.projectNumber);
          const bNum = parseInt(b.projectNumber);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return bNum - aNum;
          }
          return b.projectNumber.localeCompare(a.projectNumber);
        });
        
      setAvailableProjects(projects);
    } catch (err) {
      console.error('Error loading projects:', err);
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Group qualifying projects into programs by shared Program ID (same pattern as Calculator).
  const programs = useMemo(() => {
    const byId = new Map();
    (availableProjects || []).forEach((p) => {
      const id = (p.programId || '').trim();
      if (!id) return;
      if (!byId.has(id)) {
        byId.set(id, {
          programId: id,
          programName: (p.programName || '').trim() || id,
          events: [],
        });
      }
      byId.get(id).events.push(p);
    });
    return Array.from(byId.values())
      .map((prog) => {
        const events = prog.events.slice().sort((a, b) => {
          const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
          const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
          return aDate - bDate;
        });
        const rawClient = (events.find((e) => e.clientName) || {}).clientName || '';
        const clientNameClean = String(rawClient).replace(/\s*\(V\d+\)\s*$/i, '').trim();
        let minStart = null;
        let maxEnd = null;
        let rangeStart = '';
        let rangeEnd = '';
        events.forEach((e) => {
          if (e.startDate) {
            const s = new Date(e.startDate).getTime();
            if (!isNaN(s) && (minStart == null || s < minStart)) {
              minStart = s;
              rangeStart = e.startDate;
            }
          }
          if (e.endDate) {
            const en = new Date(e.endDate).getTime();
            if (!isNaN(en) && (maxEnd == null || en > maxEnd)) {
              maxEnd = en;
              rangeEnd = e.endDate;
            }
          }
        });
        return { ...prog, events, clientName: clientNameClean, rangeStart, rangeEnd };
      })
      .sort((a, b) => {
        const ac = (a.clientName || a.programName || a.programId).toLowerCase();
        const bc = (b.clientName || b.programName || b.programId).toLowerCase();
        return ac.localeCompare(bc);
      });
  }, [availableProjects]);

  const selectedProgram = useMemo(
    () => programs.find((prog) => prog.programId === selectedProgramId) || null,
    [programs, selectedProgramId]
  );

  useEffect(() => {
    if (selectedProgram) {
      setIncludedEventProjects(selectedProgram.events.map((e) => e.projectNumber));
      setClientName(selectedProgram.clientName || selectedProgram.programName || '');
    } else {
      setIncludedEventProjects([]);
    }
  }, [selectedProgramId, selectedProgram]);

  const toggleIncludedEvent = (pn) => {
    setIncludedEventProjects((prev) =>
      prev.includes(pn) ? prev.filter((x) => x !== pn) : [...prev, pn]
    );
  };

  const resetExpenseForm = () => {
    setProjectNumber('');
    setSelectedProgramId('');
    setIncludedEventProjects([]);
    setClientName('');
    setReportDate(getTodayDate());
    setReportPreparedBy('');
    setDeliverySupervisor('');
    setDeliveryTeamMembers('');
    setLodgingRooms('');
    setLodgingNights('');
    setLodgingTotal('');
    setGasTotal('');
    setFlightsCount('');
    setFlightsTotal('');
    setPerDiemSupervisors('');
    setPerDiemTeamMembers('');
    setPerDiemTotal('');
    setPerDiemTotalManuallySet(false);
    setTruckRentalRequired('');
    setTruckRentalCount('');
    setTruckRentalTotal('');
    setOtherExpenses([{ name: '', total: '' }]);
    setNotes('');
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '';
    
    // Parse a date string as a local date (not UTC) to avoid timezone shifts
    const parseLocalDate = (dateStr) => {
      if (!dateStr) return null;
      
      // If it's already a Date object, extract components directly
      if (dateStr instanceof Date) {
        return {
          year: dateStr.getFullYear(),
          month: dateStr.getMonth(),
          day: dateStr.getDate()
        };
      }
      
      // If it's a YYYY-MM-DD string, parse as local date
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return { year, month: month - 1, day }; // month is 0-indexed
      }
      
      // If it's an ISO string, extract date part and parse as local
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        const datePart = dateStr.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        return { year, month: month - 1, day };
      }
      
      // Try parsing as Date, but extract local components
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate()
      };
    };
    
    const formatDate = (dateParts) => {
      if (!dateParts) return '';
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[dateParts.month]} ${dateParts.day}, ${dateParts.year}`;
    };
    
    const startParts = parseLocalDate(startDate);
    const endParts = parseLocalDate(endDate);
    
    if (!startParts && !endParts) return '';
    if (!endParts) return formatDate(startParts);
    if (!startParts) return formatDate(endParts);
    
    // If same day, just return single date: "October 9, 2026"
    if (startParts.year === endParts.year && 
        startParts.month === endParts.month && 
        startParts.day === endParts.day) {
      return formatDate(startParts);
    }
    
    // If same month and year, format as "November 13-20, 2026"
    if (startParts.month === endParts.month && 
        startParts.year === endParts.year) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      return `${months[startParts.month]} ${startParts.day}-${endParts.day}, ${startParts.year}`;
    }
    
    // Different months/years: "November 13, 2026 - December 5, 2026"
    return `${formatDate(startParts)} - ${formatDate(endParts)}`;
  };

  const handleAddOtherExpense = () => {
    setOtherExpenses([...otherExpenses, { name: '', total: '' }]);
  };

  const handleRemoveOtherExpense = (index) => {
    if (otherExpenses.length > 1) {
      setOtherExpenses(otherExpenses.filter((_, i) => i !== index));
    }
  };

  const handleOtherExpenseChange = (index, field, value) => {
    const updated = [...otherExpenses];
    if (field === 'total') {
      updated[index][field] = parseCurrencyInput(value);
    } else {
      updated[index][field] = value;
    }
    setOtherExpenses(updated);
  };

  const calculateTotal = () => {
    const lodging = parseFloat(lodgingTotal) || 0;
    const gas = parseFloat(gasTotal) || 0;
    const flights = parseFloat(flightsTotal) || 0;
    const perDiem = parseFloat(perDiemTotal) || 0;
    const truckRental = parseFloat(truckRentalTotal) || 0;
    const other = otherExpenses.reduce((sum, exp) => sum + (parseFloat(exp.total) || 0), 0);
    return lodging + gas + flights + perDiem + truckRental + other;
  };

  const handleSave = async () => {
    const isProgram = expenseMode === 'program';

    if (!reportDate) {
      alert('Please fill in Report Date');
      return;
    }

    if (isProgram) {
      if (!selectedProgramId || !selectedProgram) {
        alert('Please select a program');
        return;
      }
      if (includedEventProjects.length === 0) {
        alert('Please include at least one event in this program expense report');
        return;
      }
    } else if (!projectNumber || !clientName) {
      alert('Please select a project and fill in Report Date');
      return;
    }

    // Check for an existing report (project- or program-keyed)
    const existingExpense = isProgram
      ? savedExpenses.find(
          (exp) =>
            (exp.type === 'program' || exp.programId) &&
            String(exp.programId || '').trim() === String(selectedProgramId).trim()
        )
      : savedExpenses.find(
          (exp) =>
            exp.projectNumber &&
            exp.projectNumber.toString().trim() === projectNumber.toString().trim() &&
            exp.type !== 'program'
        );

    if (existingExpense) {
      const label = isProgram
        ? `program ${selectedProgram.programName || selectedProgramId}`
        : `project ${projectNumber}`;
      const confirmOverwrite = window.confirm(
        `An expense report already exists for ${label}.\n\n` +
          `Report Date: ${existingExpense.reportDate || 'N/A'}\n` +
          `Prepared By: ${existingExpense.reportPreparedBy || 'N/A'}\n\n` +
          `Do you want to overwrite it with this new report?`
      );
      if (!confirmOverwrite) return;
    }

    setSaving(true);
    try {
      const includedEvents = isProgram
        ? selectedProgram.events
            .filter((e) => includedEventProjects.includes(e.projectNumber))
            .map((e) => ({
              projectNumber: e.projectNumber,
              venueName: e.venueName || '',
              clientName: e.clientName || '',
              status: e.status || '',
              startDate: e.startDate || '',
              endDate: e.endDate || '',
            }))
        : null;

      const expenseData = {
        timestamp: new Date().toISOString(),
        type: isProgram ? 'program' : 'project',
        projectNumber: isProgram ? '' : projectNumber,
        programId: isProgram ? selectedProgram.programId : '',
        programName: isProgram
          ? selectedProgram.programName || selectedProgram.programId
          : '',
        clientName: isProgram
          ? selectedProgram.clientName || selectedProgram.programName || clientName
          : clientName,
        reportDate,
        reportPreparedBy,
        deliverySupervisor,
        deliveryTeamMembers,
        lodging: {
          rooms: parseInt(lodgingRooms) || 0,
          nights: parseInt(lodgingNights) || 0,
          total: parseFloat(lodgingTotal) || 0,
        },
        gas: {
          total: parseFloat(gasTotal) || 0,
        },
        flights: {
          count: parseInt(flightsCount) || 0,
          total: parseFloat(flightsTotal) || 0,
        },
        perDiem: {
          supervisors: parseInt(perDiemSupervisors) || 0,
          teamMembers: parseInt(perDiemTeamMembers) || 0,
          total: parseFloat(perDiemTotal) || 0,
        },
        truckRental: {
          required: truckRentalRequired === 'yes',
          count: parseInt(truckRentalCount) || 0,
          total: parseFloat(truckRentalTotal) || 0,
        },
        otherExpenses: otherExpenses.filter((exp) => exp.name || exp.total),
        totalCost: calculateTotal(),
        notes,
        programData: isProgram
          ? {
              includedCount: includedEvents.length,
              includedEvents,
            }
          : null,
      };

      if (onSaveExpense) {
        await onSaveExpense(expenseData);
        setSaving(false);
        setSuccessMessage('Expense Report Submitted');
        setTimeout(() => setSuccessMessage(''), 3000);
        resetExpenseForm();
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save expenses. Please try again.');
      setSaving(false);
    }
  };

  const renderEstimateBanner = (estimates, heading = 'Delivery Fee Estimate') => {
    if (!estimates.length) return null;
    const latestEstimate = estimates[0];
    let totalFee = latestEstimate.totalFee;
    if (totalFee === null || totalFee === undefined) {
      const standardFee =
        latestEstimate.standardFee === 'waived' || latestEstimate.standardFee === null
          ? 0
          : latestEstimate.standardFee || 0;
      const extendedFee = latestEstimate.extendedFee || 0;
      totalFee = standardFee + extendedFee;
    } else if (totalFee === 'waived') {
      totalFee = 'Waived';
    }
    const estimateDate = latestEstimate.timestamp
      ? formatDisplayInstantDate(latestEstimate.timestamp)
      : '';
    const eventType = latestEstimate.eventType || 'Standard';

    return (
      <div className="expenses-estimate">
        <div className="expenses-estimate-label">{heading}</div>
        <div className="expenses-estimate-value">
          {typeof totalFee === 'number' ? (
            <>
              $
              {totalFee.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </>
          ) : (
            totalFee
          )}
          {estimateDate && (
            <span className="expenses-estimate-meta">Estimated {estimateDate}</span>
          )}
        </div>
        {eventType && eventType !== 'Standard' && (
          <div className="expenses-estimate-meta">Type: {eventType}</div>
        )}
        {estimates.length > 1 && (
          <div className="expenses-estimate-meta">
            {estimates.length - 1} other estimate{estimates.length - 1 !== 1 ? 's' : ''} available
          </div>
        )}
      </div>
    );
  };

  const canSave =
    !saving &&
    !!reportDate &&
    !!truckRentalRequired &&
    (expenseMode === 'program'
      ? !!selectedProgramId && includedEventProjects.length > 0
      : !!projectNumber && !!clientName);

  return (
    <div className="calculator-shell">
      <div className="calc-section calc-section--selection">
          <div className="calc-tabs" role="tablist" aria-label="Expense report type">
            <button
              type="button"
              role="tab"
              aria-selected={expenseMode === 'project'}
              className={`calc-tab ${expenseMode === 'project' ? 'active' : ''}`}
              onClick={() => {
                setExpenseMode('project');
                setSelectedProgramId('');
                setIncludedEventProjects([]);
              }}
            >
              Individual Project
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={expenseMode === 'program'}
              className={`calc-tab ${expenseMode === 'program' ? 'active' : ''}`}
              onClick={() => {
                setExpenseMode('program');
                setProjectNumber('');
              }}
            >
              Program
            </button>
          </div>

          {expenseMode === 'project' ? (
            <div className="form-section" style={{ marginBottom: 0 }}>
              <label className="form-label">
                Project <span className="required">*</span>
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <SearchableSelect
                    options={availableProjects.map((p) => {
                      const clientNameDisplay = p.clientName
                        ? p.clientName.replace(/\s*\(V\d+\)\s*$/i, '').trim()
                        : '';
                      const dateRange = formatDateRange(p.startDate, p.endDate);
                      const venue = p.venueName || '';
                      const label = p.label ? String(p.label).trim() : '';
                      const parts = [clientNameDisplay, venue, label, dateRange].filter(Boolean);
                      return {
                        value: p.projectNumber,
                        label: parts.join(' - '),
                      };
                    })}
                    value={projectNumber}
                    onChange={(value) => {
                      setProjectNumber(value);
                      if (value) {
                        setSelectedProgramId('');
                        const selected = availableProjects.find((p) => p.projectNumber === value);
                        if (selected) setClientName(selected.clientName);
                      } else {
                        setClientName('');
                      }
                    }}
                    placeholder="Search or select project..."
                    loading={loadingProjects}
                  />
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setProjectNumber('');
                    setClientName('');
                  }}
                  style={{ padding: '14px 20px', fontSize: '12px', whiteSpace: 'nowrap' }}
                >
                  Clear
                </button>
              </div>
              {loadingProjects && (
                <div className="helper-text" style={{ marginTop: '8px', color: 'var(--brand-text-dark)', fontSize: '14px' }}>
                  Loading projects...
                </div>
              )}
              {!loadingProjects && availableProjects.length === 0 && (
                <div className="helper-text" style={{ marginTop: '8px' }}>
                  No projects found. Projects appear when marked Completed, or Confirmed with a past event date.
                </div>
              )}
              {projectNumber && clientName && !loadingProjects && (
                <div className="helper-text" style={{ marginTop: '8px', color: 'var(--brand-text-dark)', fontSize: '14px' }}>
                  Selected: <strong>{projectNumber}</strong> - {clientName}
                </div>
              )}
              {projectNumber &&
                savedCalculations.length > 0 &&
                renderEstimateBanner(
                  savedCalculations
                    .filter(
                      (calc) =>
                        calc.type !== 'program' &&
                        calc.projectNumber &&
                        String(calc.projectNumber).trim() === String(projectNumber).trim()
                    )
                    .sort((a, b) => {
                      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                      return bTime - aTime;
                    })
                )}
            </div>
          ) : (
            <div className="form-section" style={{ marginBottom: 0 }}>
              <span className="helper-text" style={{ display: 'block', marginBottom: 12 }}>
                Submit one expense report for shared costs across multiple linked events.
              </span>
              <label className="form-label">
                Program <span className="required">*</span>
              </label>
              <SearchableSelect
                options={programs.map((prog) => {
                  const dateRange = formatDateRange(prog.rangeStart, prog.rangeEnd);
                  const parts = [
                    prog.clientName || prog.programName,
                    dateRange,
                    `${prog.events.length} Event${prog.events.length !== 1 ? 's' : ''}`,
                  ].filter(Boolean);
                  return { value: prog.programId, label: parts.join(' • ') };
                })}
                value={selectedProgramId}
                onChange={(value) => {
                  setSelectedProgramId(value || '');
                  if (value) setProjectNumber('');
                }}
                placeholder={
                  loadingProjects
                    ? 'Loading programs…'
                    : programs.length
                      ? 'Search programs...'
                      : 'No programs available yet'
                }
                loading={loadingProjects}
              />
              {loadingProjects && (
                <div className="helper-text" style={{ marginTop: '8px', color: 'var(--brand-text-dark)', fontSize: '14px' }}>
                  Loading programs…
                </div>
              )}
              {!loadingProjects && programs.length === 0 && (
                <div className="helper-text" style={{ marginTop: '8px', color: 'var(--brand-text-dark)', fontSize: '14px' }}>
                  No qualifying programs found. Programs appear here when linked events are Completed, or Confirmed with a past event date.
                </div>
              )}

              {selectedProgram && (
                <div className="program-summary" style={{ marginTop: 20 }}>
                  <div className="program-summary-head">
                    <div className="program-summary-head-row">
                      <p className="program-summary-title">Included Events</p>
                      <span className="program-summary-count">
                        {includedEventProjects.length} of {selectedProgram.events.length} Events Included
                      </span>
                    </div>
                    <span className="helper-text" style={{ margin: 0 }}>
                      Uncheck any event to exclude it from this expense report.
                    </span>
                  </div>
                  <table className="program-summary-table">
                    <thead>
                      <tr>
                        <th className="program-summary-checkbox" aria-label="Include" />
                        <th>Event</th>
                        <th>Dates</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedProgram.events.map((ev) => {
                        const checked = includedEventProjects.includes(ev.projectNumber);
                        const dateRange = formatDateRange(ev.startDate, ev.endDate);
                        return (
                          <tr key={ev.projectNumber} className={checked ? '' : 'excluded'}>
                            <td className="program-summary-checkbox">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleIncludedEvent(ev.projectNumber)}
                                aria-label={`Include ${ev.venueName || ev.clientName || ev.projectNumber}`}
                              />
                            </td>
                            <td>
                              <span className="program-event-name">
                                {ev.venueName || ev.clientName || ev.projectNumber}
                              </span>
                              <span className="program-event-meta">#{ev.projectNumber}</span>
                            </td>
                            <td>{dateRange || '—'}</td>
                            <td>{ev.status || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedProgramId &&
                savedCalculations.length > 0 &&
                renderEstimateBanner(
                  savedCalculations
                    .filter(
                      (calc) =>
                        calc.type === 'program' &&
                        String(calc.programId || '').trim() === String(selectedProgramId).trim()
                    )
                    .sort((a, b) => {
                      const aVersion = a.version || 1;
                      const bVersion = b.version || 1;
                      if (bVersion !== aVersion) return bVersion - aVersion;
                      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                      return bTime - aTime;
                    }),
                  'Program Delivery Fee Estimate'
                )}
            </div>
          )}
      </div>

      <div className="calc-section calc-form">
        <h3 className="section-header">Report Details</h3>

        <div className="form-section">
          <label className="form-label">
            Report Date <span className="required">*</span>
          </label>
          <input
            type="date"
            className="input-field"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
          />
        </div>

        <h3 className="section-header">Team information</h3>

        <div className="form-section">
          <label className="form-label">Report Prepared By</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter name"
            value={reportPreparedBy}
            onChange={(e) => setReportPreparedBy(e.target.value)}
          />
        </div>

        <div className="form-section">
          <label className="form-label">Delivery Supervisor</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter supervisor name"
            value={deliverySupervisor}
            onChange={(e) => setDeliverySupervisor(e.target.value)}
          />
        </div>

        <div className="form-section">
          <label className="form-label">Delivery Team Members</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter team member names (comma-separated)"
            value={deliveryTeamMembers}
            onChange={(e) => setDeliveryTeamMembers(e.target.value)}
          />
        </div>

        <h3 className="section-header">Expense categories</h3>

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">Lodging</label>
          <div className="form-row" style={{ marginBottom: '0' }}>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="number"
                className="input-field"
                placeholder="# of Rooms"
                value={lodgingRooms}
                onChange={(e) => setLodgingRooms(e.target.value)}
                min="0"
              />
            </div>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="number"
                className="input-field"
                placeholder="# of Nights"
                value={lodgingNights}
                onChange={(e) => setLodgingNights(e.target.value)}
                min="0"
              />
            </div>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="text"
                className="input-field"
                placeholder="$0.00"
                inputMode="decimal"
                value={lodgingTotal !== '' ? formatCurrencyInput(lodgingTotal) : ''}
                onChange={(e) => setLodgingTotal(parseCurrencyInput(e.target.value))}
                onBlur={(e) => setLodgingTotal(normalizeCurrencyOnBlur(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">Gas</label>
          <input
            type="text"
            className="input-field"
            placeholder="$0.00"
            inputMode="decimal"
            value={gasTotal !== '' ? formatCurrencyInput(gasTotal) : ''}
            onChange={(e) => setGasTotal(parseCurrencyInput(e.target.value))}
            onBlur={(e) => setGasTotal(normalizeCurrencyOnBlur(e.target.value))}
          />
        </div>

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">Flights</label>
          <div className="form-row" style={{ marginBottom: '0' }}>
            <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
              <input
                type="number"
                className="input-field"
                placeholder="# of Flights"
                value={flightsCount}
                onChange={(e) => setFlightsCount(e.target.value)}
                min="0"
              />
            </div>
            <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
              <input
                type="text"
                className="input-field"
                placeholder="$0.00"
                inputMode="decimal"
                value={flightsTotal !== '' ? formatCurrencyInput(flightsTotal) : ''}
                onChange={(e) => setFlightsTotal(parseCurrencyInput(e.target.value))}
                onBlur={(e) => setFlightsTotal(normalizeCurrencyOnBlur(e.target.value))}
              />
            </div>
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">Per Diem / Day Rate</label>
          <div className="helper-text" style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--brand-text-dark)' }}>
            ${PER_DIEM_SUPERVISOR_RATE} for supervisors, ${PER_DIEM_TEAM_MEMBER_RATE} for delivery team members × number of days
            {(() => {
              const nights = parseInt(lodgingNights) || 0;
              const days = nights > 0 ? nights + 1 : 1;
              return nights > 0 ? ` (${nights} night${nights !== 1 ? 's' : ''} = ${days} day${days !== 1 ? 's' : ''})` : ' (1 day default)';
            })()}
          </div>
          <div className="form-row" style={{ marginBottom: '0' }}>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="number"
                className="input-field"
                placeholder="# of Supervisors"
                value={perDiemSupervisors}
                onChange={(e) => {
                  setPerDiemSupervisors(e.target.value);
                  setPerDiemTotalManuallySet(false);
                }}
                min="0"
              />
            </div>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="number"
                className="input-field"
                placeholder="# of Team Members"
                value={perDiemTeamMembers}
                onChange={(e) => {
                  setPerDiemTeamMembers(e.target.value);
                  setPerDiemTotalManuallySet(false);
                }}
                min="0"
              />
            </div>
            <div className="form-section form-section-third" style={{ marginBottom: '0' }}>
              <input
                type="text"
                className="input-field"
                placeholder="$0.00"
                inputMode="decimal"
                value={perDiemTotal !== '' ? formatCurrencyInput(perDiemTotal) : ''}
                onChange={(e) => {
                  setPerDiemTotal(parseCurrencyInput(e.target.value));
                  setPerDiemTotalManuallySet(true);
                }}
                onBlur={(e) => {
                  setPerDiemTotal(normalizeCurrencyOnBlur(e.target.value));
                  setPerDiemTotalManuallySet(true);
                }}
              />
              {perDiemTotalManuallySet && (
                <span className="helper-text" style={{ fontSize: '12px', color: 'rgb(136, 136, 136)', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                  Manual override active. Change supervisor/team member counts to auto-calculate.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">
            Did we rent a truck for this project? <span className="required">*</span>
          </label>
          <select
            className="select-field"
            value={truckRentalRequired}
            onChange={(e) => {
              setTruckRentalRequired(e.target.value);
              if (e.target.value === 'no') {
                setTruckRentalCount('');
                setTruckRentalTotal('');
              }
            }}
          >
            <option value="">Select...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {truckRentalRequired === 'yes' && (
          <div className="form-section" style={{ marginBottom: '16px' }}>
            <label className="form-label">Truck Rental</label>
            <div className="form-row" style={{ marginBottom: '0' }}>
              <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                <input
                  type="number"
                  className="input-field"
                  placeholder="# of Trucks"
                  value={truckRentalCount}
                  onChange={(e) => setTruckRentalCount(e.target.value)}
                  min="1"
                />
              </div>
              <div className="form-section form-section-half" style={{ marginBottom: '0' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder="$0.00"
                  inputMode="decimal"
                  value={truckRentalTotal !== '' ? formatCurrencyInput(truckRentalTotal) : ''}
                  onChange={(e) => setTruckRentalTotal(parseCurrencyInput(e.target.value))}
                  onBlur={(e) => setTruckRentalTotal(normalizeCurrencyOnBlur(e.target.value))}
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-section" style={{ marginBottom: '16px' }}>
          <label className="form-label">Other Expenses</label>
          {otherExpenses.map((expense, index) => (
            <div key={index} className="form-row" style={{ marginBottom: '12px', alignItems: 'flex-start' }}>
              <div className="form-section" style={{ flex: 1, marginRight: '12px', marginBottom: '0' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Expense name"
                  value={expense.name}
                  onChange={(e) => handleOtherExpenseChange(index, 'name', e.target.value)}
                />
              </div>
              <div className="form-section" style={{ flex: '0 0 200px', marginRight: '12px', marginBottom: '0' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder="$0.00"
                  inputMode="decimal"
                  value={expense.total !== '' ? formatCurrencyInput(expense.total) : ''}
                  onChange={(e) => handleOtherExpenseChange(index, 'total', e.target.value)}
                  onBlur={(e) => {
                    handleOtherExpenseChange(index, 'total', normalizeCurrencyOnBlur(e.target.value));
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemoveOtherExpense(index)}
                disabled={otherExpenses.length === 1}
                style={{ 
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: 500,
                  fontFamily: '"Neue Haas Unica", Inter, sans-serif',
                  backgroundColor: 'rgb(250, 248, 243)',
                  color: 'rgb(0, 0, 0)',
                  border: '1px solid rgb(232, 232, 227)',
                  borderRadius: '20px',
                  cursor: otherExpenses.length === 1 ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: '0.2s',
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px',
                  minWidth: 'auto',
                  opacity: otherExpenses.length === 1 ? 0.5 : 1
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddOtherExpense}
            style={{ 
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: '"Neue Haas Unica", Inter, sans-serif',
              backgroundColor: 'rgb(250, 248, 243)',
              color: 'rgb(0, 0, 0)',
              border: '1px solid rgb(232, 232, 227)',
              borderRadius: '20px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: '0.2s',
              boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px',
              marginTop: '0px'
            }}
          >
            + Add Other Expense
          </button>
        </div>

        <div className="form-section">
          <label className="form-label">Notes</label>
          <textarea
            className="input-field"
            placeholder="Additional notes about expenses..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="3"
            style={{ resize: 'vertical', minHeight: '80px' }}
          />
        </div>

        <div className="expenses-total">
          <span className="expenses-total-label">Total Expenses</span>
          <span className="expenses-total-value">
            ${calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        {successMessage && (
          <div className="expenses-success" role="status">
            {successMessage}
          </div>
        )}

        <button
          type="button"
          className="btn-calculate"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving...' : 'Save Expenses'}
        </button>
      </div>
    </div>
  );
}

export default Expenses;
